type EntityId = number | string

export const EntityLocation = {
    AccountLevel: 'account-level',
    Ad: 'ad',
    AdGroup: 'adgroup',
    AdSet: 'adset',
    Asset: 'asset',
    AssetGroup: 'asset-group',
    Campaign: 'campaign',
    CampaignGroup: 'campaign-group',
    ConversionAction: 'conversion-action',
    Demographic: 'demographic',
    Device: 'device',
    Experiment: 'experiment',
    Extension: 'extension',
    Keyword: 'keyword',
    Location: 'location',
    Network: 'network',
    NGram: 'n-gram',
    NegativeKeyword: 'negative-keyword',
    NegativeList: 'negative-list',
    Pause: 'pause',
    Placement: 'placement',
    PlacementExclusionList: 'placement-exclusion-list',
    Product: 'product',
    ProductGroup: 'product-group',
    ReplacementKeyword: 'replacement-keyword',
    SearchTerm: 'search-term',
    URL: 'url',
    Word: 'word',
} as const
export type EntityLocation = (typeof EntityLocation)[keyof typeof EntityLocation]

export const CriterionLocation = {
    [EntityLocation.Demographic]: EntityLocation.Demographic,
    [EntityLocation.Device]: EntityLocation.Device,
    [EntityLocation.Keyword]: EntityLocation.Keyword,
    [EntityLocation.Location]: EntityLocation.Location,
    [EntityLocation.NegativeKeyword]: EntityLocation.NegativeKeyword,
} as const

export const ViewLocation = {
    [EntityLocation.ProductGroup]: EntityLocation.ProductGroup,
} as const

export const LocationWithResourceName = {
    ...CriterionLocation,
    ...ViewLocation,
    [EntityLocation.Ad]: EntityLocation.Ad,
    [EntityLocation.AdGroup]: EntityLocation.AdGroup,
    [EntityLocation.Asset]: EntityLocation.Asset,
    [EntityLocation.AssetGroup]: EntityLocation.AssetGroup,
    [EntityLocation.Campaign]: EntityLocation.Campaign,
    [EntityLocation.ConversionAction]: EntityLocation.ConversionAction,
    [EntityLocation.Experiment]: EntityLocation.Experiment,
    [EntityLocation.Extension]: EntityLocation.Extension,
    [EntityLocation.NegativeList]: EntityLocation.NegativeList,
    [EntityLocation.SearchTerm]: EntityLocation.SearchTerm,
} as const
export type LocationWithResourceName =
    (typeof LocationWithResourceName)[keyof typeof LocationWithResourceName]

type CampaignIdOnlyEntity =
    | typeof EntityLocation.Campaign
    | typeof EntityLocation.Experiment
    | typeof EntityLocation.Location

type CampaignIdAndAdGroupIdEntity =
    | typeof EntityLocation.Ad
    | typeof EntityLocation.AdGroup
    | typeof EntityLocation.Keyword
    | typeof EntityLocation.ProductGroup

type CampaignIdAndOptionalAdGroupIdEntity = typeof EntityLocation.SearchTerm

type SharedSetIdOnlyEntity =
    | typeof EntityLocation.NegativeList
    | typeof EntityLocation.PlacementExclusionList

export const NoIdsEntity = [
    EntityLocation.AccountLevel,
    EntityLocation.AdSet,
    EntityLocation.CampaignGroup,
    EntityLocation.ConversionAction,
    EntityLocation.Network,
    EntityLocation.NGram,
    EntityLocation.NegativeKeyword,
    EntityLocation.Placement,
    EntityLocation.URL,
    EntityLocation.Word,
]
export type NoIdsEntity = (typeof NoIdsEntity)[number]

type RequiredIdsHandler<T extends EntityLocation> = T extends CampaignIdOnlyEntity
    ? {
          campaignId: EntityId
          adGroupId?: null
          sharedSetId?: null
      }
    : T extends CampaignIdAndAdGroupIdEntity
      ? {
            campaignId: EntityId
            adGroupId: EntityId
            sharedSetId?: null
        }
      : T extends CampaignIdAndOptionalAdGroupIdEntity
        ? {
              campaignId: EntityId
              adGroupId?: EntityId
              sharedSetId?: null
          }
        : T extends SharedSetIdOnlyEntity
          ? {
                campaignId?: null
                adGroupId?: null
                sharedSetId: EntityId
            }
          : // Default to no params for: account-level, conversion-action, etc.
            {
                campaignId?: null
                adGroupId?: null
                sharedSetId?: null
            } | null

export type Ids<T extends EntityLocation> = T extends NoIdsEntity
    ? { entityIds?: null }
    : { entityIds: RequiredIdsHandler<T> }
